import React from 'react';
import { Button, Stack, DialogTitle, Typography, Box } from "@mui/material";
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const PenDataInfo = styled(Stack)({
  gap: 40,
  alignItems: "center",
  justifyContent: "center",
  width: "40vw",
  backgroundColor: "rgba(17, 17, 17, 0.8)",
  '& .MuiDialogTitle-root': {
    textAlign: "center",
    color: "#fff",
    fontSize: "2rem",
    fontWeight: 800,
    lineHeight: "140%",
  },
});

const ButtonWrap = styled(Stack)({
  flexDirection: "row",
  gap: 16,
  'button': {
    fontSize: 16,
    borderRadius: 8,
    width: 170,
    height: 48,
  }
});

const TipBox = styled(Stack)({
  gap: 16,
  textAlign: "center",
  color: "#fff",
  padding: 24,
  borderRadius: 16,
  border: "1px solid #fff",
  '&.warning': {
    border: "1px solid #FF0000",
    backgroundColor: "rgba(255, 0, 0, 0.4)",
  },
});

const CustomLinearProgress = styled(LinearProgress)(() => ({
  height: 20,
  borderRadius: 100,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#DEF8F5",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 100,
    backgroundColor: "#008C73",
  },
}));

export type SendingStatus = 'idle' | 'sending' | 'stoppedwithError' | 'completed';

export type SendingPanelProps = {
  i18next: ReturnType<typeof useTranslation>;
  sendingStatus: SendingStatus;
  onClose: () => void;
  onSend: () => void;
  numOfReadyToUploadPen: number;
  /** When true, disable sending due to duplicate student selections detected */
  hasDuplicateSelection?: boolean;
  /**
   * Optional: current global index and totals provided by the batch processor.
   * If provided along with itemsPerPhase, progress will reflect actual pipeline
   * progress across three equally-weighted phases (USB, GAI, Answer).
   */
  currentIndex?: number;
  total?: number;
  /**
   * Optional: number of items per phase (i.e., items.length). Used to determine
   * phase boundaries: [0..seg) => USB, [seg..2*seg) => GAI, [2*seg..3*seg) => Answer
   */
  itemsPerPhase?: number;
};

// ===== Pure helpers for debugging/breakpoints =====
export function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

export function getPhaseByIndex(currentIndex: number, itemsPerPhase: number): 'usb' | 'gai' | 'answer' | 'done' {
  const seg = itemsPerPhase ?? 0;
  const usbEnd = seg;
  const gaiEnd = seg * 2;
  const answerEnd = seg * 3;
  const idx = currentIndex;
  if (idx < usbEnd) return 'usb';
  if (idx < gaiEnd) return 'gai';
  if (idx < answerEnd) return 'answer';
  return 'done';
}

export function computeThreePhaseProgress(params: { currentIndex: number; itemsPerPhase: number; total?: number }): {
  usbPortion: number;
  gaiPortion: number;
  answerPortion: number;
  percent: number; // 0..100
} {
  const { currentIndex, itemsPerPhase } = params;
  const seg = itemsPerPhase ?? 0;
  const usbEnd = seg;
  const gaiEnd = seg * 2;
  const answerEnd = seg * 3;

  const usbPortion = seg > 0 ? clamp01((Math.min(currentIndex, usbEnd) - 0) / seg) : 0;
  const gaiPortion = seg > 0 ? clamp01((Math.min(currentIndex, gaiEnd) - usbEnd) / seg) : 0;
  const answerPortion = seg > 0 ? clamp01((Math.min(currentIndex, answerEnd) - gaiEnd) / seg) : 0;
  const percent = ((usbPortion + gaiPortion + answerPortion) / 3) * 100;
  return { usbPortion, gaiPortion, answerPortion, percent };
}

export function SendingPanel(props: SendingPanelProps) {
  const { i18next, sendingStatus, onClose, onSend, numOfReadyToUploadPen, hasDuplicateSelection, currentIndex, total, itemsPerPhase } = props;

  // Derived pipeline progress (three phases equally weighted) if indices are provided
  const seg = itemsPerPhase ?? 0;
  const usbEnd = seg;
  const gaiEnd = seg * 2;
  const answerEnd = seg * 3;

  const hasPipelineNumbers = typeof currentIndex === 'number' && typeof total === 'number' && seg > 0 && total > 0;
  const computed = React.useMemo(() => {
    if (!hasPipelineNumbers) return undefined;
    return computeThreePhaseProgress({ currentIndex: currentIndex as number, itemsPerPhase: seg, total: total as number });
  }, [hasPipelineNumbers, currentIndex, seg, total]);
  const computedPercent = typeof computed?.percent === 'number' ? Math.round(computed.percent) : undefined;

  const percent = typeof computedPercent === 'number' ? computedPercent : 0;

  // Phase/job name from currentIndex and segment boundaries
  const jobName = React.useMemo(() => {
    if (!hasPipelineNumbers) return i18next.t('진행 중');
    const idx = currentIndex as number;
    if (idx < usbEnd) return '필기 데이터 업로드';
    if (idx < gaiEnd) return '필기 인식';
    if (idx < answerEnd) return '답안 제출';
    return i18next.t('완료');
  }, [hasPipelineNumbers, currentIndex, usbEnd, gaiEnd, answerEnd, i18next]);

  return (
    <PenDataInfo>
      <DialogTitle>
        {sendingStatus === 'idle' && i18next.t("스마트 펜에서 데이터 업로드")}
        {sendingStatus === 'sending' && `${jobName} ${i18next.t("진행")}`}
        {sendingStatus === 'idle' && (
          <Typography variant="h6">{i18next.t("{{count}}개의 펜 데이터를 전송합니다.", { count: numOfReadyToUploadPen })}</Typography>
        )}
        {sendingStatus === 'sending' && (
          <Typography variant="h6">{`${jobName} ${i18next.t("중입니다.")}`}</Typography>
        )}
      </DialogTitle>
      {sendingStatus === 'idle' && (
        <ButtonWrap>
          <Button size="large" variant="outlined" style={{borderColor: "#fff", color: "#fff" }} onClick={onClose}>
            {i18next.t("취소")}
          </Button>
          <Button
            size="large"
            variant="contained"
            onClick={onSend}
            disabled={numOfReadyToUploadPen === 0 || !!hasDuplicateSelection}
            sx={{
              backgroundColor: '#025949',
              '&:hover': { backgroundColor: '#014538' },
              '&.Mui-disabled': { backgroundColor: '#6B7D78', color: '#B0B8B5', opacity: 1 },
            }}
          >
            {i18next.t("전송하기")}
          </Button>
        </ButtonWrap>
      )}
      {sendingStatus === 'sending' && (
        <Box sx={{minWidth: 430}}>
          <CustomLinearProgress variant="determinate" value={percent} />
          <Typography variant="h5" sx={{ mt: 1, textAlign: 'center', color: '#fff' }}>
            {Math.round(percent)}%
          </Typography>
        </Box>
      )}
      {sendingStatus === 'idle' && (
        <TipBox>
          <Typography variant="h5">⭐️ TIP ⭐️</Typography>
          <Typography variant="h6">크래들 왼쪽부터 펜을 하나씩 <br />천천히 끼워주세요.</Typography>
        </TipBox>
      )}

      {sendingStatus === 'sending' && (
        <TipBox className="warning">
          <Typography variant="h5">🚨 경고 🚨</Typography>
          <Typography variant="h6">펜을 크래들에서 분리하면<br />데이터가 유실될 수 있습니다.</Typography>
        </TipBox>
      )}
    </PenDataInfo>
  );
}


